import { Text } from "use-client-chakra-ui";
var pluralize = require('pluralize');

const WebSocketErrorCodes = {
  AUTH_ERROR: 4001,
  INSUFFICIENT_BALANCE: 4002,
  FILE_INTEGRITY_ERROR: 4003,
  VALIDATION_ERROR: 4004,
  SERVER_ERROR: 4005,
  NO_WORKERS: 4006,
  TIMEOUT: 4007,
  MODEL_ERROR: 4008,
  CLIENT_DISCONNECTED: 4009,
} as const;

interface ToastProps {
  title: string;
  description: string | JSX.Element;
  status: "error" | "warning" | "success" | "info";
  duration: number;
  isClosable: boolean;
}

interface ToastFunction {
  (props: ToastProps): void;
}

export function getStrapiMedia(url: string | null): string | null {
  if (url == null) {
    return null;
  }
  if (url.startsWith('http') || url.startsWith('//')) {
    return url;
  }
  return `${process.env.NEXT_PUBLIC_STRAPI_API_URL || 'https://growing-star-e0137ea205.strapiapp.com'}${url}`;
}

export const handleWebSocketError = (event: CloseEvent, toast: ToastFunction): boolean => {
  let title = "Error";
  let description: string | JSX.Element = event.reason || "An unknown error occurred";
  let status: "error" | "warning" = "error";

  switch (event.code) {
    case WebSocketErrorCodes.AUTH_ERROR:
      title = "Authentication Error";
      description = "Please sign in again to continue";
      break;

    case WebSocketErrorCodes.INSUFFICIENT_BALANCE:
      // Handle with existing insufficient balance logic
      return true;

    case WebSocketErrorCodes.FILE_INTEGRITY_ERROR:
      title = "File Integrity Error";
      description = "The required file for generating the image is corrupted or unavailable. Please try again later or contact support.";
      break;

    case WebSocketErrorCodes.VALIDATION_ERROR:
      title = "Invalid Request";
      status = "warning";
      break;

    case WebSocketErrorCodes.NO_WORKERS:
      title = "Service Unavailable";
      description = "No workers are currently available. Please try again in a few minutes.";
      status = "warning";
      break;

    case WebSocketErrorCodes.TIMEOUT:
      title = "Request Timeout";
      description = "The request took too long to process. Please try again.";
      break;

    case WebSocketErrorCodes.MODEL_ERROR:
      title = "Model Error";
      description = "There was an error with the AI model. Please try again or try a different model.";
      break;

    case WebSocketErrorCodes.CLIENT_DISCONNECTED:
      title = "Connection Lost";
      description = "Your connection was interrupted. Please check your internet connection and try again.";
      status = "warning";
      break;

    default:
      if (event.code === 1011) {
        // Keep existing maintenance message
        description = (
          <Text>
            Apologies, our servers are currently down for maintenance.
            Please check our{" "}
            <a
              target="_blank"
              style={{ textDecoration: "underline" }}
              href="https://discord.gg/CMwtccKthy"
            >
              Discord
            </a>{" "}
            for further updates
          </Text>
        );
      }
  }

  toast({
    title,
    description,
    status,
    duration: 5000,
    isClosable: true,
  });

  return false;
};
export function getStrapiURL(path: string): string {
  return `${
    process.env.NEXT_PUBLIC_STRAPI_API_URL || 'https://growing-star-e0137ea205.strapiapp.com'
  }/api${path}`;
}

interface Redirection {
  redirect: {
    destination: string;
    permanent: boolean;
  };
}

export function handleRedirection(preview: boolean, custom?: string): Redirection {
  if (preview) {
    return {
      redirect: {
        destination: `/api/exit-preview`,
        permanent: false,
      },
    };
  } else if (custom) {
    return {
      redirect: {
        destination: `/${custom}`,
        permanent: false,
      },
    };
  } else {
    return {
      redirect: {
        destination: `/`,
        permanent: false,
      },
    };
  }
}

interface GetDataResult {
  data: string;
  slug: string | null;
}

export function getData(slug: string | null, locale: string, apiID: string, kind: string, preview: boolean): GetDataResult {
  const previewParams = preview
    ? '&publicationState=preview&published_at_null=true'
    : '';

  if (kind === 'collectionType') {
    let prefix = `/${pluralize(apiID)}`;
    if (apiID === 'page') {
      prefix = ``;
    } else if (apiID === 'article') {
      prefix = `/blog`;
    }
    else if (apiID === 'news') {
      prefix = `/news`;
    }
    const slugToReturn = `${prefix}/${slug}?lang=${locale}`;
    const apiUrl = `/${pluralize(
      apiID
    )}?filters[slug][$eq]=${slug}&locale=${locale}${previewParams}&populate[blocks][populate]=members.picture,header,buttons.link,faq,featuresCheck,cards,pricingCards.perks,newses,author.picture,images,cards.image,image&populate=localizations&populate[seo][populate]=metaSocial.image`;

    return {
      data: getStrapiURL(apiUrl),
      slug: slugToReturn,
    };
  } else {
    const apiUrl = `/${apiID}?locale=${locale}${previewParams}&populate[blocks][populate]=*,buttons.link&populate=localizations&populate[header]=*&populate[seo]=metaSocial`;

    if (apiID.includes('-page')) {
      const slugToReturn =
        apiID === 'blog-page'
          ? `/${apiID.replace('-page', '')}?lang=${locale}`
          : apiID === 'news-page' ? `/${apiID.replace('-page', '')}?lang=${locale}`  : `/${apiID.replace('-page', 's')}?lang=${locale}`;
      return {
        data: getStrapiURL(apiUrl),
        slug: slugToReturn,
      };
    } else {
      return {
        data: getStrapiURL(apiUrl),
        slug: `/${apiID}?lang=${locale}`,
      };
    }
  }
}

interface Key {
  queryKey: [
    string,
    { category?: string | null },
    { locale?: string },
    { page: number },
    { perPage: number }
  ];
}

export async function getArticles(key: Key) {
  const categoryName = key.queryKey[1].category;
  const localeCode = key.queryKey[2].locale;
  const pageNumber = key.queryKey[3].page;
  const perPage = key.queryKey[4].perPage;
  const STRAPI_KEY = process.env.STRAPI_KEY;

  const start = pageNumber === 1 ? 0 : (pageNumber - 1) * perPage;

  let baseUrl = getStrapiURL(
    `/articles?pagination[limit]=${perPage}&pagination[start]=${start}&pagination[withCount]=true&populate=image,thumbnail,category,author,seo&sort=published_on%3Adesc`
  );

  if (categoryName) {
    baseUrl = `${baseUrl}&filters[category][name][$eq]=${categoryName}`;
  }

  if (localeCode) {
    baseUrl = `${baseUrl}&locale=${localeCode}`;
  }

  const res = await fetch(baseUrl, {
    headers: {
      Authorization: `Bearer ${STRAPI_KEY}`,
    },
  });
  const articles = await res.json();

  return { articles: articles.data, count: articles.meta.pagination.total };
}

export async function getNewses(key: Key) {
  const categoryName = key.queryKey[1].category;
  const localeCode = key.queryKey[2].locale;
  const pageNumber = key.queryKey[3].page;
  const perPage = key.queryKey[4].perPage;
  const STRAPI_KEY = process.env.STRAPI_KEY;

  const start = pageNumber === 1 ? 0 : (pageNumber - 1) * perPage;

  let baseUrl = getStrapiURL(
    `/newses?pagination[limit]=${perPage}&pagination[start]=${start}&pagination[withCount]=true&populate=image,thumbnail,category,author,seo&sort=published_on%3Adesc`
  );

  if (categoryName) {
    baseUrl = `${baseUrl}&filters[category][name][$eq]=${categoryName}`;
  }

  if (localeCode) {
    baseUrl = `${baseUrl}&locale=${localeCode}`;
  }

  const res = await fetch(baseUrl, {
    headers: {
      Authorization: `Bearer ${STRAPI_KEY}`,
    },
  });
  const newses = await res.json();

  return { newses: newses.data, count: newses.meta.pagination.total };
}